<template>
    <div class="crud-area">
        <div class="container-fluid">
            <!-- Cabeçalho -->
            <div class="crud-header d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 class="crud-title">Notícias</h2>
                    <p class="crud-subtitle">Gerir as notícias da fundação</p>
                </div>
                <button class="btn-primary-grad" @click="openCreate">
                    <i class="ri-add-line"></i> Nova Notícia
                </button>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary"></div>
            </div>

            <!-- Erro -->
            <div v-if="error" class="alert-error mb-3">
                <i class="ri-error-warning-line"></i> {{ error }}
            </div>

            <!-- Tabela -->
            <div v-if="!loading" class="crud-card">
                <div class="table-responsive">
                    <table class="crud-table">
                        <thead>
                            <tr>
                                <th>Título (PT)</th>
                                <th>Data Publicação</th>
                                <th>Leitura (min)</th>
                                <th class="text-end">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="items.length === 0">
                                <td colspan="4" class="text-center text-muted py-4">Sem notícias registadas.</td>
                            </tr>
                            <tr v-for="item in items" :key="item.id">
                                <td>{{ item.title_pt }}</td>
                                <td>{{ formatDate(item.publishDate) }}</td>
                                <td>{{ item.readTime || '—' }} min</td>
                                <td class="text-end">
                                    <button class="btn-icon edit" @click="openEdit(item)" title="Editar">
                                        <i class="ri-edit-line"></i>
                                    </button>
                                    <button class="btn-icon delete" @click="confirmDelete(item)" title="Eliminar">
                                        <i class="ri-delete-bin-line"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <!-- Paginação -->
                <div class="crud-pagination d-flex justify-content-between align-items-center mt-3"
                    v-if="total > limit">
                    <span class="text-muted small">{{ total }} notícias</span>
                    <div class="d-flex gap-2">
                        <button class="btn-page" :disabled="page === 1" @click="changePage(page - 1)">‹</button>
                        <span class="page-info">{{ page }} / {{ totalPages }}</span>
                        <button class="btn-page" :disabled="page >= totalPages" @click="changePage(page + 1)">›</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Criar / Editar -->
        <div v-if="showModal" class="crud-modal-overlay" @click.self="closeModal">
            <div class="crud-modal">
                <div class="crud-modal-header">
                    <h3>{{ editing ? 'Editar Notícia' : 'Nova Notícia' }}</h3>
                    <button class="btn-close-modal" @click="closeModal"><i class="ri-close-line"></i></button>
                </div>
                <form @submit.prevent="save" class="crud-modal-body">
                    <div class="row g-3">
                        <div class="col-12">
                            <label>Título (PT) *</label>
                            <input v-model="form.title_pt" class="form-input" required
                                placeholder="Título em português" />
                        </div>
                        <div class="col-12">
                            <label>Título (EN)</label>
                            <input v-model="form.title_en" class="form-input" placeholder="Title in English" />
                        </div>
                        <div class="col-12">
                            <label>Conteúdo (PT) *</label>
                            <textarea v-model="form.content_pt" class="form-input" rows="4" required
                                placeholder="Conteúdo em português"></textarea>
                        </div>
                        <div class="col-12">
                            <label>Conteúdo (EN)</label>
                            <textarea v-model="form.content_en" class="form-input" rows="4"
                                placeholder="Content in English"></textarea>
                        </div>
                        <div class="col-6">
                            <label>Tempo de leitura (min)</label>
                            <input v-model.number="form.readTime" class="form-input" type="number" min="1"
                                placeholder="Ex: 5" />
                        </div>
                        <div class="col-6">
                            <label>Data de Publicação</label>
                            <input v-model="form.publishDate" class="form-input" type="date" />
                        </div>
                        <div class="col-12">
                            <label>Imagem de Capa</label>
                            <ImageUpload :value="form.coverImageUrl" @update:value="form.coverImageUrl = $event" />
                        </div>

                        <!-- Push Notification -->
                        <div class="col-12 mt-3" v-if="!editing">
                            <div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" id="sendPush" v-model="form.sendPush">
                                <label class="form-check-label ms-2" for="sendPush">
                                    Enviar Push Notification para todos os dispositivos
                                </label>
                            </div>
                        </div>
                    </div>
                    <div class="crud-modal-footer mt-4">
                        <button type="button" class="btn-secondary" @click="closeModal">Cancelar</button>
                        <button type="submit" class="btn-primary-grad" :disabled="saving">
                            <span v-if="saving"><i class="ri-loader-4-line spin"></i> A guardar...</span>
                            <span v-else>{{ editing ? 'Guardar' : 'Criar' }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Confirmação de Eliminar -->
        <div v-if="showDelete" class="crud-modal-overlay" @click.self="showDelete = false">
            <div class="crud-modal crud-modal--sm">
                <div class="crud-modal-header">
                    <h3>Eliminar Notícia</h3>
                    <button class="btn-close-modal" @click="showDelete = false"><i class="ri-close-line"></i></button>
                </div>
                <div class="crud-modal-body">
                    <p>Tem a certeza que deseja eliminar <strong>{{ selectedItem?.title_pt }}</strong>?</p>
                </div>
                <div class="crud-modal-footer">
                    <button class="btn-secondary" @click="showDelete = false">Cancelar</button>
                    <button class="btn-danger" @click="deleteItem" :disabled="saving">
                        <span v-if="saving"><i class="ri-loader-4-line spin"></i></span>
                        <span v-else>Eliminar</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import newsService from '../../services/newsService';
import ImageUpload from '../Common/ImageUpload.vue';

export default {
    name: 'NewsContent',
    components: { ImageUpload },
    data() {
        return {
            items: [],
            loading: true,
            saving: false,
            error: '',
            page: 1,
            limit: 10,
            total: 0,
            showModal: false,
            showDelete: false,
            editing: false,
            selectedItem: null,
            form: { title_pt: '', title_en: '', content_pt: '', content_en: '', readTime: null, publishDate: '', coverImageUrl: '', sendPush: false },
        };
    },
    computed: {
        totalPages() { return Math.ceil(this.total / this.limit); },
    },
    mounted() {
        this.load();
    },
    methods: {
        async load() {
            this.loading = true; this.error = '';
            try {
                const res = await newsService.getAll(this.page, this.limit);
                this.items = res.data || res;
                this.total = res.total || this.items.length;
            } catch (e) { this.error = 'Erro ao carregar notícias.'; }
            finally { this.loading = false; }
        },
        formatDate(d) {
            if (!d) return '—';
            return new Date(d).toLocaleDateString('pt-PT');
        },
        changePage(p) { this.page = p; this.load(); },
        openCreate() {
            this.editing = false;
            this.form = { title_pt: '', title_en: '', content_pt: '', content_en: '', readTime: null, publishDate: '', coverImageUrl: '', sendPush: false };
            this.showModal = true;
        },
        openEdit(item) {
            this.editing = true;
            this.selectedItem = item;
            const d = item.publishDate ? item.publishDate.split('T')[0] : '';
            this.form = { title_pt: item.title_pt, title_en: item.title_en || '', content_pt: item.content_pt, content_en: item.content_en || '', readTime: item.readTime, publishDate: d, coverImageUrl: item.coverImageUrl || '' };
            this.showModal = true;
        },
        closeModal() { this.showModal = false; },
        async save() {
            this.saving = true;
            try {
                const payload = { ...this.form };
                if (payload.publishDate) payload.publishDate = new Date(payload.publishDate).toISOString();
                if (this.editing) { await newsService.update(this.selectedItem.id, payload); }
                else { await newsService.create(payload); }
                this.closeModal(); this.load();
            } catch (e) { this.error = 'Erro ao guardar notícia.'; }
            finally { this.saving = false; }
        },
        confirmDelete(item) { this.selectedItem = item; this.showDelete = true; },
        async deleteItem() {
            this.saving = true;
            try {
                await newsService.remove(this.selectedItem.id);
                this.showDelete = false; this.load();
            } catch (e) { this.error = 'Erro ao eliminar notícia.'; }
            finally { this.saving = false; }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../Dashboards/GgmfDashboard/ggmf-card';
</style>
